import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { patientApi } from '../api';
import Spinner from '../../../components/ui/Spinner';
import OfficialDocumentLayout from '../components/documents/OfficialDocumentLayout';
import {
  CertificateTemplate, PrescriptionTemplate, LabReportTemplate,
  DischargeSummaryTemplate, AmbulanceCertificateTemplate,
  ECCEmergencyCertificateTemplate, BloodDonationCertificateTemplate,
  InsuranceClaimPackTemplate, InvoiceReceiptTemplate,
  VaccinationCertificateTemplate, AppointmentReceiptTemplate,
  PharmacyDocumentTemplate, HospitalDocumentTemplate,
  AmbulanceInvoiceTemplate, BloodBankDocumentTemplate,
  InsuranceDocumentTemplate, LabDocumentTemplate, PaymentReceiptTemplate,
  TEMPLATE_MAP,
} from '../templates';

// Maps the vault document's stored metadata onto the shape its template
// expects. Only real recorded metadata is rendered — fields the source module
// did not record stay blank. Money fields default to 0 because the templates
// format them unconditionally.
function buildTemplateDataForCategory(category: string, metadata: any) {
  const m = metadata || {};
  const signatories = m.signatories
    || (m.issuedBy ? [{ name: m.issuedBy, role: 'Issuing Authority', institution: m.institutionName }] : []);

  switch (category) {
    case 'LAB_REPORT':
      return {
        labName: m.labName,
        labNhcId: m.labNhcId,
        sampleId: m.sampleId,
        sampleType: m.sampleType,
        collectedAt: m.collectedAt,
        reportedAt: m.reportedAt,
        tests: m.tests || [],
        interpretation: m.interpretation,
        methodology: m.methodology,
        signatories,
      };
    case 'PRESCRIPTION':
      return {
        diagnosis: m.diagnosis,
        complaints: m.complaints,
        medicines: m.medicines || [],
        advice: m.advice || [],
        doctorName: m.doctorName,
        speciality: m.speciality,
        signatories,
      };
    case 'DISCHARGE_SUMMARY':
      return {
        admissionDate: m.admissionDate,
        dischargeDate: m.dischargeDate,
        wardBed: m.wardBed,
        admittingDoctor: m.admittingDoctor,
        diagnosis: m.diagnosis || [],
        procedures: m.procedures || [],
        hospitalCourse: m.hospitalCourse,
        conditionAtDischarge: m.conditionAtDischarge,
        medications: m.medications || [],
        followUpInstructions: m.followUpInstructions || [],
        emergencyInstructions: m.emergencyInstructions,
        signatories,
      };
    case 'INVOICE':
      return {
        invoiceId: m.invoiceId,
        invoiceDate: m.invoiceDate,
        paymentStatus: m.paymentStatus,
        paymentMode: m.paymentMode,
        transactionId: m.transactionId,
        billedTo: m.billedTo,
        items: m.items || [],
        subtotal: m.subtotal ?? 0,
        gstTotal: m.gstTotal ?? 0,
        insuranceCovered: m.insuranceCovered ?? 0,
        grandTotal: m.grandTotal ?? 0,
        amountPaid: m.amountPaid ?? 0,
        balanceDue: m.balanceDue ?? 0,
        notes: m.notes,
        signatories,
      };
    case 'INSURANCE_DOCUMENT':
    case 'CLAIM_DOCUMENT':
      return {
        claimId: m.claimId,
        policyNumber: m.policyNumber,
        insurerName: m.insurerName,
        planName: m.planName,
        claimType: m.claimType,
        claimAmount: m.claimAmount ?? 0,
        approvedAmount: m.approvedAmount ?? 0,
        claimStatus: m.claimStatus,
        admissionDate: m.admissionDate,
        dischargeDate: m.dischargeDate,
        diagnosis: m.diagnosis || [],
        procedures: m.procedures || [],
        documents: m.documents || [],
        remarks: m.remarks,
        signatories,
      };
    case 'AMBULANCE_RECORD':
      return {
        dispatchId: m.dispatchId,
        callType: m.callType,
        priority: m.priority,
        pickupLocation: m.pickupLocation,
        dropLocation: m.dropLocation,
        dispatchTime: m.dispatchTime,
        arrivalTime: m.arrivalTime,
        totalTime: m.totalTime,
        distance: m.distance,
        crewParamedic: m.crewParamedic,
        ambulanceNo: m.ambulanceNo,
        patientCondition: m.patientCondition || [],
        equipment: m.equipment || [],
        handoverNotes: m.handoverNotes,
        signatories,
      };
    case 'BLOOD_BANK_RECORD':
      return {
        donationId: m.donationId,
        donationType: m.donationType,
        bloodGroup: m.bloodGroup,
        units: m.units,
        donationDate: m.donationDate,
        venue: m.venue,
        campName: m.campName,
        hemoglobin: m.hemoglobin,
        bpReading: m.bpReading,
        bloodBankName: m.bloodBankName,
        bloodBankLicense: m.bloodBankLicense,
        componentsExtracted: m.componentsExtracted || [],
        nextEligibleDate: m.nextEligibleDate,
        signatories,
      };
    case 'VACCINATION_RECORD':
      return {
        vaccines: m.vaccines || [],
        postInstructions: m.postInstructions || [],
        signatories,
      };
    default:
      return {
        certificateType: category,
        body: m.body || '',
        clinicalDetails: m.clinicalDetails || [],
        signatories,
      };
  }
}

function renderByCategory(category: string, data: any) {
  switch (category) {
    case 'LAB_REPORT': return <LabReportTemplate data={data} />;
    case 'PRESCRIPTION': return <PrescriptionTemplate data={data} />;
    case 'DISCHARGE_SUMMARY': return <DischargeSummaryTemplate data={data} />;
    case 'INVOICE': return <InvoiceReceiptTemplate data={data} />;
    case 'AMBULANCE_RECORD': return <AmbulanceCertificateTemplate data={data} />;
    case 'ECC_EMERGENCY_RECORD': return <ECCEmergencyCertificateTemplate data={data} />;
    case 'BLOOD_BANK_RECORD': return <BloodDonationCertificateTemplate data={data} />;
    case 'INSURANCE_DOCUMENT':
    case 'CLAIM_DOCUMENT': return <InsuranceClaimPackTemplate data={data} />;
    case 'VACCINATION_RECORD': return <VaccinationCertificateTemplate data={data} />;
    case 'APPOINTMENT_RECEIPT': return <AppointmentReceiptTemplate data={data} />;
    case 'PHARMACY_DOCUMENT': return <PharmacyDocumentTemplate data={data} />;
    case 'HOSPITAL_DOCUMENT': return <HospitalDocumentTemplate data={data} />;
    case 'AMBULANCE_INVOICE': return <AmbulanceInvoiceTemplate data={data} />;
    case 'BLOOD_BANK_DOCUMENT': return <BloodBankDocumentTemplate data={data} />;
    case 'INSURANCE_CLAIM_DOCUMENT': return <InsuranceDocumentTemplate data={data} />;
    case 'LAB_DOCUMENT': return <LabDocumentTemplate data={data} />;
    case 'PAYMENT_RECEIPT': return <PaymentReceiptTemplate data={data} />;
    default: {
      const mapped = TEMPLATE_MAP[category];
      if (mapped === 'AppointmentReceiptTemplate') return <AppointmentReceiptTemplate data={data} />;
      if (mapped === 'PharmacyDocumentTemplate') return <PharmacyDocumentTemplate data={data} />;
      if (mapped === 'HospitalDocumentTemplate') return <HospitalDocumentTemplate data={data} />;
      if (mapped === 'AmbulanceInvoiceTemplate') return <AmbulanceInvoiceTemplate data={data} />;
      if (mapped === 'BloodBankDocumentTemplate') return <BloodBankDocumentTemplate data={data} />;
      if (mapped === 'InsuranceDocumentTemplate') return <InsuranceDocumentTemplate data={data} />;
      if (mapped === 'LabDocumentTemplate') return <LabDocumentTemplate data={data} />;
      if (mapped === 'PaymentReceiptTemplate') return <PaymentReceiptTemplate data={data} />;
      return <CertificateTemplate data={data} />;
    }
  }
}

const CAT_LABELS: Record<string, string> = {
  LAB_REPORT: 'Lab Report', PRESCRIPTION: 'Prescription', DISCHARGE_SUMMARY: 'Discharge Summary',
  INVOICE: 'Invoice / Receipt', AMBULANCE_RECORD: 'Ambulance Record', ECC_EMERGENCY_RECORD: 'Emergency Record',
  BLOOD_BANK_RECORD: 'Blood Bank Record', INSURANCE_DOCUMENT: 'Insurance Document', CLAIM_DOCUMENT: 'Claim Document',
  VACCINATION_RECORD: 'Vaccination Record', CERTIFICATE: 'Certificate', IMAGING_REPORT: 'Imaging Report',
  UPLOADED_RECORD: 'Uploaded Record', NEOVERIFY_DOCUMENT: 'NeoVerify Document',
};

// Only identifiers actually recorded on the document or its metadata are
// listed — nothing is synthesized client-side.
function buildLinkedIdsForVault(_category: string, doc: any, metadata: any): { label: string; value: string }[] {
  const m = metadata || {};
  const ids: { label: string; value: string }[] = [];
  const push = (label: string, value: any) => { if (value) ids.push({ label, value: String(value) }); };

  push('Patient NHC ID', doc.nhcId);
  push('Document ID', doc.sourceRecordId || doc.id);
  push('NeoVerify ID', doc.neoVerifyDocumentId || doc.linkedCertificate?.neoVerifyId);

  push('Doctor NHC ID', m.doctorNhcId);
  push('Appointment ID', m.appointmentId);
  push('Hospital NHC ID', m.hospitalNhcId);
  push('Admission ID', m.admissionId);
  push('Lab NHC ID', m.labNhcId);
  push('Sample ID', m.sampleId);
  push('Lab Report ID', m.labReportId);
  push('Invoice ID', m.invoiceId);
  push('Transaction ID', m.transactionId);
  push('Ambulance Provider ID', m.ambulanceProviderId);
  push('Vehicle ID', m.ambulanceNo);
  push('Dispatch ID', m.dispatchId);
  push('Blood Bank ID', m.bloodBankNhcId);
  push('Donation ID', m.donationId);
  push('Insurance Provider ID', m.insurerNhcId);
  push('Claim ID', m.claimId);
  push('Policy ID', m.policyNumber);

  ids.push({ label: 'Generated by', value: 'NeoHealthCard' });
  return ids;
}

export default function DocumentPreview() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!documentId) return;
    let alive = true;
    setLoading(true);
    patientApi.getDocumentPreview(documentId)
      .then(d => { if (alive) setDoc(d); })
      .catch(() => { if (alive) setError('Document not found or access denied'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [documentId]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  if (error || !doc) return (
    <div className="max-w-2xl mx-auto text-center py-20">
      <p className="text-red-500 mb-4">{error || 'Not found'}</p>
      <button onClick={() => navigate(-1)} className="text-teal-600 hover:underline text-sm">Go back</button>
    </div>
  );

  const metadata = doc.previewData || doc.metadata || {};
  const templateData = buildTemplateDataForCategory(doc.category, metadata);
  const linkedIds = buildLinkedIdsForVault(doc.category, doc, metadata);

  const category = doc.category;

  // Compliance and provider identifiers come exclusively from the source
  // module's recorded metadata — the layout hides any field that is absent.
  const compliance = {
    gstin: metadata.gstin,
    pan: metadata.pan,
    stateCode: metadata.stateCode,
  };

  const providerIds: any = {};
  if (category === 'PRESCRIPTION') {
    providerIds.nhcProviderId = metadata.doctorNhcId;
    providerIds.registrationNo = metadata.doctorLicense;
    providerIds.nmc = metadata.nmc;
    providerIds.qualification = metadata.qualification;
    providerIds.specialty = metadata.speciality;
  } else if (category === 'LAB_REPORT') {
    providerIds.nhcProviderId = metadata.labNhcId;
    providerIds.nablCertNo = metadata.nablCertNo;
    providerIds.isoCertNo = metadata.isoCertNo;
    providerIds.pathologistId = metadata.pathologistId;
  } else if (category === 'AMBULANCE_RECORD') {
    providerIds.nhcProviderId = metadata.ambulanceProviderId;
    providerIds.vehicleRegNo = metadata.ambulanceNo;
    providerIds.paramedicId = metadata.paramedicId;
  } else if (category === 'INSURANCE_DOCUMENT' || category === 'CLAIM_DOCUMENT') {
    providerIds.nhcProviderId = metadata.insurerNhcId;
    providerIds.irdaiRegNo = metadata.irdaiRegNo;
    providerIds.tpaId = metadata.tpaId;
  } else if (category === 'BLOOD_BANK_RECORD') {
    providerIds.nhcProviderId = metadata.bloodBankNhcId;
    providerIds.bloodBankLicenseNo = metadata.bloodBankLicense;
    providerIds.fdaLicenseNo = metadata.fdaLicenseNo;
  } else {
    providerIds.nhcProviderId = metadata.issuerNhcId;
    providerIds.registrationNo = metadata.hospitalRegNo;
    providerIds.accreditations = metadata.accreditations;
  }

  const header = {
    institutionName: metadata.institutionName || doc.issuedBy || 'Issuer not recorded',
    institutionType: metadata.institutionType,
    institutionAddress: metadata.institutionAddress,
    institutionPhone: metadata.institutionPhone,
    institutionEmail: metadata.institutionEmail,
    accreditation: metadata.accreditation,
    registrationNo: metadata.registrationNo,
    compliance,
    providerIds,
  };

  const certData = doc.linkedCertificate;

  return (
    <div className="max-w-5xl mx-auto">
      <button onClick={() => navigate(-1)} className="no-print flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <OfficialDocumentLayout
        documentTitle={doc.title}
        documentNumber={certData?.certificateId || doc.sourceRecordId || doc.id.slice(0, 20)}
        documentType={CAT_LABELS[doc.category] || doc.category.replace(/_/g, ' ')}
        issueDate={doc.issuedAt || doc.createdAt}
        expiryDate={doc.expiresAt}
        status={certData?.status || (doc.verificationStatus === 'VERIFIED' ? 'VERIFIED' : 'ISSUED')}
        header={header}
        patientName={doc.patientName || 'Patient'}
        patientAge={doc.patientAge}
        patientGender={doc.patientGender}
        nhcId={doc.nhcId}
        neoVerifyId={doc.neoVerifyDocumentId || certData?.neoVerifyId}
        verifyUrl={doc.verifyUrl || certData?.verifyUrl}
        qrPayload={doc.qrPayload || certData?.qrPayload}
        documentHash={doc.documentHash || certData?.documentHash}
        verificationStatus={doc.verificationStatus}
        registeredAt={certData?.neoVerifyDoc?.registeredAt || doc.createdAt}
        linkedIds={linkedIds}
        watermark={!doc.isActive ? 'REVOKED' : null}
        onPrint={() => window.print()}
        onDownload={() => window.print()}
        onShare={() => navigate(`/patient/share-center`)}
      >
        {renderByCategory(doc.category, templateData)}
      </OfficialDocumentLayout>
    </div>
  );
}
